import type { WithT } from 'i18next';
import { escape, flow } from 'lodash-es';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { connect } from 'react-redux';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';

import { AuthActions } from 'common/auth/actions';
import { Form } from 'common/component/ValidationFormAndField';
import withTranslate from 'common/i18n/i18n';
import { mustMatch, required, validPassword } from 'common/utils/validations';
import { PasswordValidIndicators } from 'common/viewcomp/display/PasswordValidIndicators';
import { CITextField } from 'common/viewcomp/forms/fields/text';

interface Props extends WithT {
    onSubmit: () => void;
    submitting: boolean;
    children: (disableButton) => ReactElement;
    changePasswordRequest: (newpass) => void;
}

const UserUpdatePasswordFormComponent = (props: Props): ReactElement => {
    const { t, onSubmit, submitting, changePasswordRequest, children } = props;
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setConfirmShowPassword] = useState(false);

    const handleClickShowPassword = (): void => {
        setShowPassword(!showPassword);
    };

    const handleShowConfirmPassword = (): void => {
        setConfirmShowPassword(!showConfirmPassword);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleSubmit = ({ newpass, confirmpass }): void => {
        changePasswordRequest(escape(newpass));
        onSubmit();
    };

    return (
        <Form
            t={t}
            onSubmit={handleSubmit}
            initialValues={{ newpass: '', confirmpass: '' }}
            validations={[mustMatch('newpass', 'confirmpass', 'confirmpass', 'password_form.must_match')]}
            render={({ handleSubmit, invalid, values: { newpass } }): ReactElement => {
                const disableButton = submitting || invalid;

                return (
                    <form id="updatePassForm" onSubmit={handleSubmit}>
                        <CITextField
                            t={t}
                            id="newpass"
                            name="newpass"
                            labelI18NKey="password_form.newpass"
                            type={showPassword ? 'text' : 'password'}
                            margin="normal"
                            fullWidth
                            trimWhiteSpaces={false}
                            required
                            maxLength={16} // temporary until specs are defined
                            validations={[required(), validPassword('error.invalid_password')]}
                            autoFocus
                            endAdornment={
                                <InputAdornment position="end" style={{ paddingBottom: '30px' }}>
                                    <IconButton
                                        aria-label={t('login.toggle_password')}
                                        onClick={handleClickShowPassword}
                                        size="large"
                                    >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        {newpass && <PasswordValidIndicators password={newpass} t={t} />}
                        <CITextField
                            t={t}
                            id="confirmpass"
                            name="confirmpass"
                            labelI18NKey="password_form.confirm_pass"
                            type={showConfirmPassword ? 'text' : 'password'}
                            margin="normal"
                            trimWhiteSpaces={false}
                            fullWidth
                            maxLength={16} // temporary until specs are defined
                            required
                            validations={[required('error.required')]}
                            endAdornment={
                                <InputAdornment position="end" style={{ paddingBottom: '30px' }}>
                                    <IconButton
                                        aria-label={t('login.toggle_password')}
                                        onClick={handleShowConfirmPassword}
                                        size="large"
                                    >
                                        {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        {/* Childrens are buttons to be rendered with the this component
                        this makes button rendering more flexible, naming and maybe cancel button etc etc */}
                        {children(disableButton)}
                    </form>
                );
            }}
        />
    );
};

const mapDispatchToProps = { ...AuthActions };

const UserUpdatePasswordForm = flow([withTranslate('auth')])(
    connect(null, mapDispatchToProps)(UserUpdatePasswordFormComponent)
);

export default UserUpdatePasswordForm;
